/**
 * Login Page Component
 *
 * Magic link request page with email input form.
 * Implements the login flow for Linkode's passwordless authentication.
 *
 * Features:
 * - Email input with client-side validation
 * - Loading, success, and error states
 * - Rate limiting error handling
 * - i18n support for all user-facing text
 * - Double-submit prevention
 * - Keyboard navigation (Tab, Enter)
 * - Responsive design
 * - Sunset Writer Theme (Storybook Design System)
 *
 * @module features/auth/pages/LoginPage
 * @see SPEC-005.5-auth-login-ui Requirements 1.1-1.9, 7.1-7.7, 10.5
 */

import { useState, useCallback, FormEvent, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTypedTranslation } from '../../../i18n';
import { useAuthStore } from '../store/authStore';
import { authApiClient } from '../api/authApiClient';
import { validateEmail } from '../utils/emailValidator';
import { ApiErrorCode, type ApiError } from '@linkode/shared';

// Design System Components
import { Button, InputField } from '../../../stories/landing';
import '../../../stories/landing/landing.css';

// ============================================================================
// Types
// ============================================================================

/**
 * Login page state machine states
 * - idle: Initial state, waiting for user input
 * - loading: API request in progress
 * - success: Magic link sent successfully
 * - error: API request failed
 */
type LoginState = 'idle' | 'loading' | 'success' | 'error';

/**
 * Location state passed from other pages (e.g., AuthGuard redirect)
 */
interface LocationState {
  from?: string;
  error?: string;
}

// ============================================================================
// Styles (Sunset Writer Theme)
// ============================================================================

const pageStyles: React.CSSProperties = {
  minHeight: '100vh',
  backgroundColor: 'var(--color-background, #f4efea)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px',
  fontFamily: "var(--font-sans, 'Inter', sans-serif)",
};

const containerStyles: React.CSSProperties = {
  width: '100%',
  maxWidth: '420px',
};

const titleStyles: React.CSSProperties = {
  textAlign: 'center',
  marginBottom: '32px',
};

const h1Styles: React.CSSProperties = {
  fontFamily: "var(--font-mono, 'JetBrains Mono', monospace)",
  fontSize: '28px',
  fontWeight: 500,
  color: 'var(--color-text-primary, #383838)',
  margin: 0,
};

const cardStyles: React.CSSProperties = {
  backgroundColor: 'var(--color-white, #ffffff)',
  border: '2px solid var(--border-color, #383838)',
  borderRadius: '2px',
  padding: '32px',
  boxShadow: 'var(--shadow-offset, -12px 12px 0px 0px #383838)',
};

const formStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
};

const fieldStyles: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const labelStyles: React.CSSProperties = {
  fontFamily: "var(--font-mono, 'JetBrains Mono', monospace)",
  fontSize: '14px',
  fontWeight: 500,
  color: 'var(--color-text-primary, #383838)',
};

const errorTextStyles: React.CSSProperties = {
  fontSize: '14px',
  color: 'var(--color-accent-red, #ef4444)',
  margin: 0,
};

const errorBoxStyles: React.CSSProperties = {
  backgroundColor: 'var(--color-bg-red-light, rgba(239, 68, 68, 0.1))',
  border: '2px solid var(--color-accent-red, #ef4444)',
  borderRadius: '2px',
  padding: '16px',
};

const warningBoxStyles: React.CSSProperties = {
  backgroundColor: 'var(--color-bg-yellow-light, #fef3c7)',
  border: '2px solid var(--color-accent-yellow, #fcd34d)',
  borderRadius: '2px',
  padding: '16px',
};

const successContainerStyles: React.CSSProperties = {
  textAlign: 'center',
};

const successIconContainerStyles: React.CSSProperties = {
  marginBottom: '16px',
};

const successIconStyles: React.CSSProperties = {
  width: '48px',
  height: '48px',
  backgroundColor: 'var(--color-bg-teal-light, rgba(20, 184, 166, 0.2))',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto',
  border: '2px solid var(--color-accent-teal, #14b8a6)',
};

const successTitleStyles: React.CSSProperties = {
  fontFamily: "var(--font-mono, 'JetBrains Mono', monospace)",
  fontSize: '18px',
  fontWeight: 500,
  color: 'var(--color-text-primary, #383838)',
  marginBottom: '8px',
};

const successHintStyles: React.CSSProperties = {
  fontSize: '14px',
  color: 'var(--color-text-secondary, #a1a1a1)',
};

const retryButtonStyles: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 500,
  color: 'var(--color-accent-red, #ef4444)',
  background: 'none',
  border: 'none',
  textDecoration: 'underline',
  cursor: 'pointer',
  padding: 0,
  marginTop: '12px',
};

// ============================================================================
// Component
// ============================================================================

/**
 * Login Page Component
 *
 * Renders the magic link request form with:
 * - Email input field with validation
 * - Submit button with loading state
 * - Success message after email sent
 * - Error messages with retry option
 *
 * @example
 * ```tsx
 * // In router configuration
 * <Route path="/login" element={<LoginPage />} />
 * ```
 *
 * @see Requirements 1.1-1.9, 7.1-7.7
 */
export function LoginPage() {
  const { t } = useTypedTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Get location state (redirect info from AuthGuard)
  const locationState = location.state as LocationState | null;

  // Auth store state
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const checkAuth = useAuthStore((state) => state.checkAuth);

  // Form state
  const [email, setEmail] = useState('');
  const [state, setState] = useState<LoginState>('idle');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<ApiError | null>(null);

  // Check auth status on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Redirect if already authenticated
  // @see Requirements 5.4 - redirect to stored destination after auth
  useEffect(() => {
    if (isAuthenticated) {
      const from = locationState?.from || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, locationState?.from, navigate]);

  /**
   * Handle form submission
   * Validates email and calls magic link API
   *
   * @param e - Form submit event
   * @see Requirements 1.3, 1.4, 1.8, 10.5
   */
  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();

      // Prevent double-submit during loading
      // @see Requirements 10.5
      if (state === 'loading') {
        return;
      }

      // Validate email
      // @see Requirements 1.8
      const validation = validateEmail(email);
      if (!validation.isValid) {
        setValidationError(validation.error ?? 'auth.validation.emailInvalid');
        return;
      }

      // Clear previous errors and set loading state
      setValidationError(null);
      setApiError(null);
      setState('loading');

      try {
        // Call magic link API
        // @see Requirements 1.3
        await authApiClient.requestMagicLink(validation.sanitized);

        // Success - show confirmation message
        // @see Requirements 1.5
        setState('success');
      } catch (err) {
        const error = err as ApiError;
        setApiError(error);
        setState('error');

        // Log error in development (without sensitive data)
        // @see Requirements 10.2
        if (import.meta.env.DEV) {
          console.error('[Auth] Magic link request failed:', {
            code: error.code,
            requestId: error.requestId,
          });
        }
      }
    },
    [email, state]
  );

  /**
   * Handle retry button click
   * Resets form to idle state for retry
   *
   * @see Requirements 1.7
   */
  const handleRetry = useCallback(() => {
    setState('idle');
    setApiError(null);
  }, []);

  /**
   * Get user-friendly error message based on API error code
   *
   * @param error - API error object
   * @returns Translated error message
   * @see Requirements 7.4, 9.1
   */
  const getErrorMessage = (error: ApiError): string => {
    switch (error.code) {
      case ApiErrorCode.RATE_LIMITED:
        // @see Requirements 1.6
        return t('auth.error.rateLimited');
      case ApiErrorCode.VALIDATION_ERROR:
        return t('auth.error.invalidEmail');
      default:
        // @see Requirements 1.7
        return t('auth.error.generic');
    }
  };

  /**
   * Handle email input change
   * Clears validation error when user starts typing
   */
  const handleEmailChange = useCallback((value: string) => {
    setEmail(value);
    // Clear validation error when user modifies input
    setValidationError(null);
  }, []);

  // Don't render if already authenticated (will redirect)
  if (isAuthenticated) {
    return null;
  }

  return (
    <div className="login-page" style={pageStyles}>
      <div className="login-container" style={containerStyles}>
        {/* Logo/Title Section */}
        <div style={titleStyles}>
          <h1 className="login-title" style={h1Styles}>
            {t('auth.login.title')}
          </h1>
        </div>

        {/* Main Card */}
        <div style={cardStyles}>
          {state === 'success' ? (
            // Success State
            // @see Requirements 1.5, 7.5
            <div className="login-success" style={successContainerStyles}>
              <div style={successIconContainerStyles}>
                <div style={successIconStyles}>
                  <svg
                    style={{
                      width: '24px',
                      height: '24px',
                      color: 'var(--color-accent-teal, #14b8a6)',
                    }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
              <p style={successTitleStyles}>{t('auth.login.successMessage')}</p>
              <p className="login-success-hint" style={successHintStyles}>
                {t('auth.login.checkEmail')}
              </p>
            </div>
          ) : (
            // Form State (idle, loading, error)
            <form onSubmit={handleSubmit} className="login-form" style={formStyles}>
              {/* Email Field */}
              <div className="form-field" style={fieldStyles}>
                <label htmlFor="email" style={labelStyles}>
                  {t('auth.login.emailLabel')}
                </label>
                <InputField
                  id="email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder={t('auth.login.emailPlaceholder')}
                  disabled={state === 'loading'}
                  autoFocus
                  autoComplete="email"
                  hasError={!!validationError}
                  aria-invalid={!!validationError}
                  aria-describedby={validationError ? 'email-error' : undefined}
                />
                {/* Validation Error */}
                {/* @see Requirements 1.9 */}
                {validationError && (
                  <p id="email-error" style={errorTextStyles} role="alert">
                    {t(validationError as Parameters<typeof t>[0])}
                  </p>
                )}
              </div>

              {/* API Error Message */}
              {/* @see Requirements 1.6, 1.7, 7.4 */}
              {state === 'error' && apiError && (
                <div className="form-error" style={errorBoxStyles}>
                  <p style={{ ...errorTextStyles, marginBottom: '0' }}>
                    {getErrorMessage(apiError)}
                  </p>
                  <button type="button" onClick={handleRetry} style={retryButtonStyles}>
                    {t('auth.login.retry')}
                  </button>
                </div>
              )}

              {/* Location State Error (from AuthGuard redirect) */}
              {locationState?.error && state === 'idle' && (
                <div style={warningBoxStyles}>
                  <p
                    style={{
                      fontSize: '14px',
                      color: 'var(--color-text-warning, #78350f)',
                      margin: 0,
                    }}
                  >
                    {t(locationState.error as Parameters<typeof t>[0])}
                  </p>
                </div>
              )}

              {/* Submit Button */}
              {/* @see Requirements 1.2, 1.4, 7.6, 10.5 */}
              <Button
                type="submit"
                variant="primary"
                fullWidth
                disabled={state === 'loading'}
                isLoading={state === 'loading'}
                aria-busy={state === 'loading'}
              >
                {state === 'loading' ? t('auth.login.submitting') : t('auth.login.submit')}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
